import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { LaunchDialogService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { NonSellableProductService } from '../../../../core/catalog';
import { BaseAbstractModalComponent, ModalCloseReason } from '../../../../core/modal';
import {
  Country,
  NonSellableProductInquiryFormContactType,
  NonSellableProductInquiryFormData,
  Product,
  User,
} from '../../../../core/model';
import { AddressFacade, PrincipalConfigurationService, UserFacade } from '../../../../core/user';
import { shallowEqualObjects } from '../../../../core/util';
import { Option } from '../../../../shared/components/dropdown/dropdown.component';
import { EmailValidator } from '../../../../shared/validators/email-validator';
import { FormArrayCheckboxesValidator } from '../../../../shared/validators/form-array-checkboxes-validator';
import { PhoneNumberValidator } from '../../../../shared/validators/phone-number-validator';

@Component({
  selector: 'py-pdp-inquiry-modal',
  templateUrl: './pdp-inquiry-modal.component.html',
  styleUrls: ['./pdp-inquiry-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdpInquiryModalComponent extends BaseAbstractModalComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  product: Product;
  sendInquiryLoading$: Observable<boolean>;
  sendInquirySuccess$: Observable<boolean>;
  user$: Observable<User>;
  phoneCodes$: Observable<Option[]>;
  modelOptions: Option[];
  form: UntypedFormGroup;
  formSubmitted = false;
  pdpInquiryContactTypes: typeof NonSellableProductInquiryFormContactType = NonSellableProductInquiryFormContactType;
  contactType: NonSellableProductInquiryFormContactType;
  phoneContactFormattedDate: string;
  timeSlots: string[];
  timeSlots$: Observable<string[]>;

  get contactTypeFormControl(): UntypedFormControl {
    return this.form?.get('contactType') as UntypedFormControl;
  }

  get anyContactDateFormControl(): UntypedFormControl {
    return this.form?.get('anyContactDate') as UntypedFormControl;
  }

  get contactDateFormControl(): UntypedFormControl {
    return this.form?.get('contactDate') as UntypedFormControl;
  }

  get anyContactTimeSlotsFormControl(): UntypedFormControl {
    return this.form?.get('anyContactTimeSlot') as UntypedFormControl;
  }

  get modelFormControl(): UntypedFormControl {
    return this.form?.get('model') as UntypedFormControl;
  }

  get contactTimeSlotsFormArray(): UntypedFormArray {
    return this.form?.get('contactTimeSlots') as UntypedFormArray;
  }

  get contactTimeSlotsFormArrayInvalid(): boolean {
    return (
      this.contactTimeSlotsFormArray.invalid &&
      (this.contactTimeSlotsFormArray.dirty ||
        this.contactTimeSlotsFormArray.controls.some((control: AbstractControl) => control.touched))
    );
  }

  constructor(
    private fb: UntypedFormBuilder,
    private addressService: AddressFacade,
    private userService: UserFacade,
    private principalConfigurationService: PrincipalConfigurationService,
    private nonSellableProductService: NonSellableProductService,
    protected launchDialogService: LaunchDialogService,
    protected el: ElementRef,
    protected renderer: Renderer2
  ) {
    super(el, launchDialogService, renderer);
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      productCode: [''],
      firstName: ['', [Validators.required, Validators.maxLength(32)]],
      lastName: ['', [Validators.required, Validators.maxLength(32)]],
      email: ['', [Validators.required, EmailValidator.singleEmail]],
      phone: ['', [Validators.required], [PhoneNumberValidator.phoneNumber(this.addressService)]],
      countryPhoneCode: ['', [Validators.maxLength(4)]],
      companyName: ['', [Validators.required, Validators.maxLength(35)]],
      contactType: [NonSellableProductInquiryFormContactType.EMAIL, [Validators.required]],
      anyContactDate: [{ value: false, disabled: true }],
      contactDate: [{ value: this.getMinPhoneContactDate(), disabled: true }],
      anyContactTimeSlot: [{ value: false, disabled: true }],
      contactTimeSlots: this.fb.array([]),
      additionalInfo: ['', [Validators.maxLength(300)]],
    });

    this.subscriptions.add(
      this.launchDialogService.data$.pipe(take(1)).subscribe((data: Product) => {
        this.product = data;
        this.form.get('productCode').setValue(data.code, { emitEvent: false });
      })
    );

    this.sendInquiryLoading$ = this.nonSellableProductService.getSendNonSellableProductInquiryLoading();
    this.sendInquirySuccess$ = this.nonSellableProductService.getSendNonSellableProductInquirySuccess();

    this.prefillFormWithUserData();

    this.updateFormWithTimeslotsFromConfig();

    this.prepareModelField();

    this.prefillFormWithPhoneCode();

    this.updateFormOnContactTypeChange();

    this.updateFormOnAnyContactDateChange();

    this.updateFormOnAnyContactTimeSlotChange();

    this.handleSendInquirySuccess();
  }

  private updateFormWithTimeslotsFromConfig(): void {
    this.timeSlots$ = this.principalConfigurationService.getValue('nonSellableInquiryFormTimeSlots').pipe(
      map((timeSlots) => (timeSlots?.value as string)?.split(',')),
      filter((timeSlots) => !!timeSlots?.length),
      take(1),
      tap((timeSlots) => {
        timeSlots.forEach((_timeSlot) =>
          this.contactTimeSlotsFormArray.push(new UntypedFormControl({ value: false, disabled: true }))
        );

        this.timeSlots = timeSlots;
      })
    );
  }

  private prepareModelField(): void {
    if (this.product?.models?.length) {
      this.form.addControl('model', new UntypedFormControl('', [Validators.required]));

      this.modelOptions = this.product.models.map((model) => ({ label: model, value: model }));
    }
  }

  private prefillFormWithPhoneCode(): void {
    this.phoneCodes$ = this.addressService.getProfileCountries().pipe(
      map((countries: Country[]) =>
        countries.map((c: Country) => {
          return { label: c.countryPhoneCode, value: c.countryPhoneCode };
        })
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscriptions.add(
      this.phoneCodes$.subscribe((phoneCodes) => {
        this.form.get('countryPhoneCode').setValue(phoneCodes[0].value, { emitEvent: false });
      })
    );
  }

  private prefillFormWithUserData(): void {
    this.user$ = this.userService.get().pipe(filter((user) => user && !!Object.keys(user).length));

    this.subscriptions.add(
      this.user$.subscribe((user) => {
        this.form.patchValue(
          {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phone: user.phone,
            countryPhoneCode: user.countryPhoneCode,
            companyName: user.address?.name1,
          },
          { emitEvent: false }
        );
      })
    );
  }

  private updateFormOnContactTypeChange(): void {
    this.subscriptions.add(
      this.contactTypeFormControl.valueChanges.subscribe((contactType) => {
        this.formSubmitted = false;

        if (contactType === NonSellableProductInquiryFormContactType.EMAIL) {
          this.anyContactDateFormControl.disable();
          this.contactDateFormControl.disable();
          this.anyContactTimeSlotsFormControl.disable();
          this.contactTimeSlotsFormArray.disable();
        }

        if (contactType === NonSellableProductInquiryFormContactType.PHONE) {
          this.anyContactDateFormControl.enable();
          this.anyContactTimeSlotsFormControl.enable();

          if (!this.anyContactDateFormControl.value) {
            this.contactDateFormControl.enable();
          }

          if (!this.anyContactTimeSlotsFormControl.value) {
            this.contactTimeSlotsFormArray.enable();
          }
        }

        const validator: ValidatorFn | [] =
          contactType === NonSellableProductInquiryFormContactType.PHONE ? Validators.required : [];

        this.anyContactDateFormControl.setValidators(validator);
        this.contactDateFormControl.setValidators(validator);
        this.anyContactTimeSlotsFormControl.setValidators(validator);
        this.contactTimeSlotsFormArray.setValidators(FormArrayCheckboxesValidator.min(1));

        this.contactTimeSlotsFormArray.updateValueAndValidity();
        this.form.updateValueAndValidity();
      })
    );
  }

  private updateFormOnAnyContactDateChange(): void {
    this.subscriptions.add(
      this.anyContactDateFormControl.valueChanges
        .pipe(distinctUntilChanged(shallowEqualObjects))
        .subscribe((anyContactDate: boolean) => {
          anyContactDate ? this.contactDateFormControl.disable() : this.contactDateFormControl.enable();
        })
    );
  }

  private updateFormOnAnyContactTimeSlotChange(): void {
    this.subscriptions.add(
      this.anyContactTimeSlotsFormControl.valueChanges
        .pipe(distinctUntilChanged(shallowEqualObjects))
        .subscribe((anyContactTimeSlot: boolean) => {
          anyContactTimeSlot ? this.contactTimeSlotsFormArray.disable() : this.contactTimeSlotsFormArray.enable();
        })
    );
  }

  private handleSendInquirySuccess(): void {
    this.subscriptions.add(
      this.sendInquirySuccess$.subscribe((success) => {
        if (success) {
          this.launchDialogService.closeDialog(ModalCloseReason.PDP_INQUIRY_SUCCESS);
        }
      })
    );
  }

  private mapFormValuesToDataObject(): NonSellableProductInquiryFormData {
    const { contactDate, ...formValues } = this.form.value;

    const data: NonSellableProductInquiryFormData = formValues;

    if (contactDate) {
      data.formattedContactDate = this.phoneContactFormattedDate;
    }

    if (formValues['contactTimeSlots']?.length) {
      const selectedTimeSlots: string[] = [];

      (formValues['contactTimeSlots'] as boolean[]).forEach((timeSlotSelected: boolean, i: number) =>
        timeSlotSelected ? selectedTimeSlots.push(this.timeSlots[i]) : null
      );

      data.contactTimeSlots = selectedTimeSlots;
    }

    return data;
  }

  // Returns "tomorrow" as the first available contact date. If this date is Saturday or Sunday, it returns the following Monday.
  getMinPhoneContactDate(): Date {
    const minDate = new Date();
    const dayOfWeek = minDate.getDay();
    let daysToAdd: number;

    switch (dayOfWeek) {
      case 5: // Friday
        daysToAdd = 3;
        break;
      case 6: // Saturday
        daysToAdd = 2;
        break;
      case 0: // Sunday
        daysToAdd = 1;
        break;
      default:
        // Monday - Thursday
        daysToAdd = 1;
    }

    minDate.setDate(minDate.getDate() + daysToAdd);

    return minDate;
  }

  onFormSubmit(): void {
    this.formSubmitted = true;

    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => control.markAsDirty());
      Object.values(this.contactTimeSlotsFormArray.controls).forEach((control) => control.markAsDirty());

      this.form.updateValueAndValidity();
      return;
    }

    const inquiryFormData: NonSellableProductInquiryFormData = this.mapFormValuesToDataObject();

    this.nonSellableProductService.sendNonSellableProductInquiry(inquiryFormData);
  }

  ngOnDestroy(): void {
    this.nonSellableProductService.resetSendNonSellableProductInquiryState();
    this.subscriptions.unsubscribe();
  }
}
